import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  ArrowLeft, ShieldQuestion, Trash2, BrainCircuit, Mail, Activity, Search,
  Lock, Ban, DollarSign, Power, Minus, Plus, Loader2, RefreshCw, ShieldCheck,
  UserPlus, HardDriveDownload, CreditCard, CheckCircle2, XCircle, Music2,
} from "lucide-react";
import BusinessCommandCard from "@/components/dashboard/BusinessCommandCard";
import ApiLedgerPanel from "@/components/ledger/ApiLedgerPanel";
import { adminApi } from "@/api/admin";
import { hubApi } from "@/api/dataHub";

/* Admin Panel — founder-only root workspace (reached via the Command Center
   dashboard → ADMIN PANEL launcher). Tiles are wired to the live /api/admin/*
   endpoints. */
const TABS = [
  { k: "status", label: "STATUS", Icon: Activity },
  { k: "users", label: "USERS", Icon: Search },
  { k: "locks", label: "LOCKS", Icon: Lock },
  { k: "bans", label: "BANS", Icon: Ban },
  { k: "ledger", label: "LEDGER", Icon: DollarSign },
];

const fmtBytes = (b) => {
  const n = Number(b) || 0;
  if (n < 1024) return `${n} B`;
  if (n < 1048576) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1048576).toFixed(2)} MB`;
};

function LimitMeter({ label, used, max, hint, testid }) {
  const pct = max ? Math.min(100, Math.round((used / max) * 100)) : 0;
  const breach = used > max;
  const full = !breach && used === max;
  const warn = !breach && !full && pct >= 85;
  const barColor = breach ? "bg-red-600" : (full || warn) ? "bg-gold" : "bg-charcoal";
  const badge = breach ? "bg-red-600 text-white" : (full || warn) ? "bg-gold text-black" : "bg-charcoal text-cream";
  const state = breach ? "⚠ BREACH — over limit"
    : full ? "● At capacity — locked"
    : warn ? "⚠ Warning — nearing limit" : hint;
  return (
    <div className="border-2 border-charcoal rounded-xl bg-cream p-3" data-testid={testid}>
      <div className="flex items-center justify-between mb-2">
        <span className="font-mono text-[9px] font-black uppercase tracking-[0.18em] text-charcoal/60">{label}</span>
        <span className={`font-mono text-[10px] font-black px-2 py-0.5 rounded border-2 border-charcoal ${badge}`} data-testid={`${testid}-count`}>
          {used} / {max}
        </span>
      </div>
      <div className="h-3 w-full border-2 border-charcoal rounded bg-white overflow-hidden">
        <div className={`h-full ${barColor}`} style={{ width: `${pct}%` }} />
      </div>
      <p className="font-mono text-[8px] uppercase tracking-wide text-charcoal/50 mt-1.5">
        {state}
      </p>
    </div>
  );
}


export default function AdminPortal({ onBack }) {
  const [otp, setOtp] = useState("");
  const [tab, setTab] = useState("status");

  // Live data streams
  const [reg, setReg] = useState(null);       // access-registry
  const [pays, setPays] = useState(null);     // payouts
  const [cfg, setCfg] = useState(null);       // system-config

  // Purge stuck builds
  const [purging, setPurging] = useState(false);
  const [freed, setFreed] = useState(null);

  // Alix Global Knowledge
  const [kbText, setKbText] = useState("");
  const [kbWeight, setKbWeight] = useState(1.0);
  const [kbSaving, setKbSaving] = useState(false);
  const [kbLoaded, setKbLoaded] = useState(false);

  // Registry & collection limits
  const [limits, setLimits] = useState(null);

  // Go-Live payments — Stripe catalogue status + seeding
  const [stripe, setStripe] = useState(null);
  const [stripeErr, setStripeErr] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [tiers, setTiers] = useState(null);   // Ad Maker tier control
  const [savingTier, setSavingTier] = useState(null);

  // Buy a Song funnel — read-only weekly/monthly totals
  const [buySong, setBuySong] = useState(null);
  const [buySongErr, setBuySongErr] = useState(false);
  const loadBuySong = () => { setBuySongErr(false); adminApi.buySongStats().then(setBuySong).catch(() => { setBuySong(null); setBuySongErr(true); }); };

  const loadStripe = () => { setStripeErr(false); adminApi.stripeStatus().then(setStripe).catch(() => { setStripe(null); setStripeErr(true); }); };

  // Personal Data Hub — global regulatory switch
  const [hubLive, setHubLive] = useState(null);
  const [hubSaving, setHubSaving] = useState(false);
  const loadHubSwitch = () => hubApi.getSwitch().then((d) => setHubLive(d.live)).catch(() => setHubLive(null));
  const toggleHub = async () => {
    const next = !hubLive;
    setHubSaving(true);
    try { const r = await hubApi.setSwitch(next); setHubLive(r.live); toast.success(next ? "Personal Data Hub is now LIVE for all users" : "Hub locked to founder only"); }
    catch { toast.error("Could not update switch"); }
    finally { setHubSaving(false); }
  };

  const seedStripe = async () => {
    setSeeding(true);
    try {
      const r = await adminApi.seedStripePrices();
      toast.success(`Stripe ${r.mode.toUpperCase()} · ${r.message}`);
      loadStripe();
    } catch (e) { toast.error(e?.response?.data?.detail || "Seed failed"); }
    finally { setSeeding(false); }
  };

  const loadStreams = () => {
    adminApi.registryStatus().then(setLimits).catch(() => setLimits(null));
    adminApi.accessRegistry().then(setReg).catch(() => setReg({ subscriber_count: 0, pending: [], pending_count: 0, whitelist: [] }));
    adminApi.payouts().then(setPays).catch(() => setPays({ payouts: [], count: 0, pending_total: 0 }));
    adminApi.systemConfig().then((c) => {
      setCfg(c);
      if (!kbLoaded) { setKbText(c?.alix_knowledge || ""); setKbWeight(c?.alix_prompt_weight ?? 1.0); setKbLoaded(true); }
    }).catch(() => setCfg({ maintenance_mode: false, rate_limit_per_min: 20 }));
    adminApi.userTiers().then((d) => setTiers(d.users || [])).catch(() => setTiers([]));
  };
  useEffect(() => { loadStreams(); loadStripe(); loadHubSwitch(); loadBuySong(); }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  const setTierField = (uid, patch) =>
    setTiers((rows) => (rows || []).map((r) => (r.user_id === uid ? { ...r, ...patch } : r)));

  const saveTier = async (row) => {
    setSavingTier(row.user_id);
    try {
      await adminApi.updateUserTier({
        target_user_id: row.user_id,
        category: row.category,
        is_founder_exempt: !!row.is_founder_exempt,
      });
      toast.success(`Updated ${row.handle || row.email || row.user_id}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
    finally { setSavingTier(null); }
  };

  const saveKnowledge = async () => {
    setKbSaving(true);
    try {
      const r = await adminApi.setKnowledge({ text: kbText, weight: kbWeight });
      setKbWeight(r.alix_prompt_weight);
      toast.success(`Alix knowledge saved · ${r.length} chars · ×${r.alix_prompt_weight}`);
    } catch { toast.error("Save failed"); }
    finally { setKbSaving(false); }
  };

  const purge = async () => {
    setPurging(true);
    try {
      const r = await adminApi.wipeTemp();
      setFreed(r);
      toast.success(`Purged ${r.scratchpads_removed} build(s) · ${fmtBytes(r.bytes_freed)} freed`);
    } catch { toast.error("Purge failed"); }
    finally { setPurging(false); }
  };

  const saveCfg = async (patch) => {
    setCfg((c) => ({ ...(c || {}), ...patch }));
    try { const r = await adminApi.setSystemConfig(patch); setCfg(r); }
    catch { toast.error("Config save failed"); }
  };

  const rl = cfg?.rate_limit_per_min ?? 20;

  return (
    <div className="absolute inset-0 z-50 flex flex-col bg-cream" data-testid="admin-portal">
      <header className="shrink-0 flex items-center gap-3 px-4 py-3 bg-charcoal border-b-2 border-gold">
        <button onClick={onBack} data-testid="admin-back" aria-label="Back" className="w-9 h-9 rounded-full bg-white/10 border-2 border-gold grid place-items-center active:scale-95"><ArrowLeft size={17} className="text-gold" /></button>
        <h1 data-testid="admin-panel-title" className="font-mono text-base font-black uppercase tracking-[0.22em] text-gold flex items-center gap-2"><ShieldQuestion size={18} /> Admin Panel</h1>
      </header>

      <div className="flex-1 min-h-0 overflow-y-auto no-scrollbar px-4 pt-4 pb-28 space-y-4">
        {/* Personal Data Hub — global regulatory switch (founder only) */}
        <div className="border-4 border-gold rounded-2xl bg-charcoal p-4 shadow-[4px_4px_0px_#1A1A1A]" data-testid="admin-hub-switch">
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <p className="font-mono text-xs font-black uppercase tracking-widest text-gold">Personal Data Hub</p>
              <p className="font-mono text-xs text-white/60 mt-1 leading-snug">
                {hubLive ? "LIVE — open to all users." : "Founder-only. Others see the compliance waitlist."}
              </p>
            </div>
            <button onClick={toggleHub} disabled={hubSaving || hubLive === null} data-testid="admin-hub-toggle"
              className={`w-16 h-9 rounded-full border-2 border-black flex items-center px-1 shrink-0 transition-colors ${hubLive ? "bg-emerald-500 justify-end" : "bg-white/15 justify-start"}`}>
              <span className="w-6 h-6 rounded-full bg-cream border-2 border-black" />
            </button>
          </div>
          <p className="font-mono text-[11px] text-white/40 mt-2">Flip one switch to roll out private secure folders to everyone.</p>
        </div>

        {/* Registry & collection limits */}
        <div className="border-4 border-charcoal rounded-2xl bg-white p-4 shadow-[4px_4px_0px_#1A1A1A]" data-testid="admin-registry-limits">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-mono text-[9px] font-black uppercase tracking-[0.2em] text-charcoal/50">Registry & Limits</p>
              <p className="font-sans font-bold text-charcoal mt-0.5">Master Hubs & tenant collections</p>
            </div>
            <button onClick={loadStreams} data-testid="admin-limits-refresh" className="w-8 h-8 grid place-items-center rounded-lg bg-cream border-2 border-charcoal shadow-[2px_2px_0px_#1A1A1A] active:translate-x-[1px] active:translate-y-[1px] active:shadow-none"><RefreshCw size={13} /></button>
          </div>
          {limits ? (
            <div className="mt-3 space-y-2.5">
              <LimitMeter label="Master Hubs (11 public + 1 hidden)" used={limits.hubs?.total ?? 0} max={limits.hubs?.max ?? 12}
                hint={`${limits.hubs?.public ?? 0} public · ${limits.hubs?.hidden ?? 0} hidden · locked at 12`} testid="admin-hub-meter" />
              <LimitMeter label="Collections (cluster ceiling)" used={limits.collections?.total ?? 0} max={limits.collections?.max ?? 500}
                hint={`${limits.collections?.remaining ?? 0} slots remaining`} testid="admin-collection-meter" />
              <div className="border-2 border-charcoal rounded-xl bg-cream p-3" data-testid="admin-tenant-footprint">
                <div className="flex items-center justify-between">
                  <span className="font-mono text-[9px] font-black uppercase tracking-[0.18em] text-charcoal/60">Your tenant footprint</span>
                  <span className="font-mono text-[10px] font-black px-2 py-0.5 rounded border-2 border-charcoal bg-charcoal text-cream">
                    {limits.tenant?.collections_with_data ?? 0} / {limits.tenant?.max ?? 500}
                  </span>
                </div>
                <p className="font-mono text-[8px] uppercase tracking-wide text-charcoal/50 mt-1.5">
                  Collections holding your user_id data (approx · scoped read)
                </p>
              </div>
            </div>
          ) : (
            <div className="mt-3 flex items-center justify-center py-4"><Loader2 size={16} className="animate-spin text-charcoal" /></div>
          )}
        </div>

        {/* Go-Live: Payments — Stripe catalogue status + one-tap seeding */}
        <div className="border-4 border-charcoal rounded-2xl bg-white p-4 shadow-[4px_4px_0px_#1A1A1A]" data-testid="admin-golive-payments">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-mono text-[9px] font-black uppercase tracking-[0.2em] text-charcoal/50 flex items-center gap-1"><CreditCard size={12} /> Go-Live · Payments</p>
              <p className="font-sans font-bold text-charcoal mt-0.5">Stripe catalogue & rails</p>
            </div>
            <button onClick={loadStripe} data-testid="admin-stripe-refresh" className="w-8 h-8 grid place-items-center rounded-lg bg-cream border-2 border-charcoal shadow-[2px_2px_0px_#1A1A1A] active:translate-x-[1px] active:translate-y-[1px] active:shadow-none"><RefreshCw size={13} /></button>
          </div>
          {stripe ? (
            <div className="mt-3 space-y-2.5" data-testid="admin-stripe-status">
              <div className="grid grid-cols-2 gap-2">
                <div className={`border-2 border-charcoal rounded-xl p-2.5 ${stripe.mode === "live" ? "bg-gold" : "bg-cream"}`} data-testid="admin-stripe-mode">
                  <p className="font-mono text-[7.5px] uppercase tracking-[0.18em] text-charcoal/60">Stripe Mode</p>
                  <p className="font-serif text-xl text-charcoal tabular-nums uppercase">{stripe.mode}</p>
                </div>
                <div className={`border-2 border-charcoal rounded-xl p-2.5 ${stripe.paypal_live ? "bg-gold" : "bg-cream"}`} data-testid="admin-paypal-live">
                  <p className="font-mono text-[7.5px] uppercase tracking-[0.18em] text-charcoal/60">PayPal</p>
                  <p className="font-serif text-xl text-charcoal tabular-nums">{stripe.paypal_live ? "LIVE" : "OFF"}</p>
                </div>
              </div>
              <div className="border-2 border-charcoal rounded-xl bg-cream p-3" data-testid="admin-stripe-prices">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-mono text-[9px] font-black uppercase tracking-[0.18em] text-charcoal/60">Price catalogue</span>
                  <span className={`font-mono text-[10px] font-black px-2 py-0.5 rounded border-2 border-charcoal ${stripe.all_present ? "bg-gold text-black" : "bg-red-600 text-white"}`}>
                    {Object.values(stripe.prices || {}).filter(Boolean).length} / {Object.keys(stripe.prices || {}).length}
                  </span>
                </div>
                <div className="grid grid-cols-1 gap-1">
                  {Object.entries(stripe.prices || {}).map(([lk, ok]) => (
                    <div key={lk} className="flex items-center gap-1.5" data-testid={`admin-price-${lk}`}>
                      {ok ? <CheckCircle2 size={12} className="text-green-600 shrink-0" /> : <XCircle size={12} className="text-red-600 shrink-0" />}
                      <span className="font-mono text-[9px] text-charcoal/70 truncate">{lk}</span>
                    </div>
                  ))}
                </div>
              </div>
              <button onClick={seedStripe} disabled={seeding} data-testid="admin-seed-stripe-btn"
                className="w-full flex items-center justify-center gap-2 bg-gold text-black font-mono text-[11px] font-black uppercase tracking-widest py-3 rounded-full border-2 border-charcoal shadow-[3px_3px_0px_#1A1A1A] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none disabled:opacity-60">
                {seeding ? <Loader2 size={15} className="animate-spin" /> : <CreditCard size={15} />} Sync Stripe Catalogue
              </button>
              <p className="font-mono text-[8px] text-charcoal/45 leading-relaxed">
                Creates any missing Products/Prices in the CURRENT Stripe account (whatever the deployed secret key points at). Idempotent — safe to re-run. Run once after switching production to live keys.
              </p>
            </div>
          ) : stripeErr ? (
            <div className="mt-3 flex flex-col items-center gap-2 py-4" data-testid="admin-stripe-error">
              <p className="font-mono text-[10px] text-red-600 font-black uppercase tracking-wide">Couldn't load Stripe status</p>
              <button onClick={loadStripe} className="font-mono text-[9px] font-black uppercase tracking-widest bg-charcoal text-gold border-2 border-charcoal rounded-full px-4 py-1.5 active:scale-95">Retry</button>
            </div>
          ) : (
            <div className="mt-3 flex items-center justify-center py-4"><Loader2 size={16} className="animate-spin text-charcoal" /></div>
          )}
        </div>

        {/* Buy a Song — read-only funnel (page visits → free lyrics → paid songs) */}
        <div className="border-4 border-charcoal rounded-2xl bg-white p-4 shadow-[4px_4px_0px_#1A1A1A]" data-testid="admin-buysong-funnel">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-mono text-[9px] font-black uppercase tracking-[0.2em] text-charcoal/50 flex items-center gap-1"><Music2 size={12} /> Buy a Song · Funnel</p>
              <p className="font-sans font-bold text-charcoal mt-0.5">/music/buyasong · visits → lyrics → paid</p>
            </div>
            <button onClick={loadBuySong} data-testid="admin-buysong-refresh" className="w-8 h-8 grid place-items-center rounded-lg bg-cream border-2 border-charcoal shadow-[2px_2px_0px_#1A1A1A] active:translate-x-[1px] active:translate-y-[1px] active:shadow-none"><RefreshCw size={13} /></button>
          </div>
          {buySong ? (
            <div className="mt-3 space-y-3" data-testid="admin-buysong-stats">
              {[{ k: "week", label: "This week · 7 days" }, { k: "month", label: "This month · 30 days" }].map(({ k, label }) => {
                const s = buySong[k] || {};
                return (
                  <div key={k} className="border-2 border-charcoal rounded-xl bg-cream p-3" data-testid={`admin-buysong-${k}`}>
                    <p className="font-mono text-[9px] font-black uppercase tracking-[0.18em] text-charcoal/60 mb-2">{label}</p>
                    <div className="grid grid-cols-4 gap-1.5">
                      <div className="border-2 border-charcoal rounded-lg bg-charcoal p-2" data-testid={`admin-buysong-${k}-views`}>
                        <p className="font-mono text-[7px] uppercase tracking-[0.12em] text-gold/70">Visits</p>
                        <p className="font-serif text-xl text-gold tabular-nums">{s.views ?? 0}</p>
                      </div>
                      <div className="border-2 border-charcoal rounded-lg bg-white p-2" data-testid={`admin-buysong-${k}-unique`}>
                        <p className="font-mono text-[7px] uppercase tracking-[0.12em] text-charcoal/50">Unique</p>
                        <p className="font-serif text-xl text-charcoal tabular-nums">{s.unique_visitors ?? 0}</p>
                      </div>
                      <div className="border-2 border-charcoal rounded-lg bg-white p-2" data-testid={`admin-buysong-${k}-lyrics`}>
                        <p className="font-mono text-[7px] uppercase tracking-[0.12em] text-charcoal/50">Lyrics</p>
                        <p className="font-serif text-xl text-charcoal tabular-nums">{s.lyrics_generated ?? 0}</p>
                      </div>
                      <div className="border-2 border-charcoal rounded-lg bg-gold p-2" data-testid={`admin-buysong-${k}-paid`}>
                        <p className="font-mono text-[7px] uppercase tracking-[0.12em] text-black/60">Paid</p>
                        <p className="font-serif text-xl text-black tabular-nums">{s.songs_paid ?? 0}</p>
                      </div>
                    </div>
                    <p className="font-mono text-[8px] text-charcoal/50 mt-2 leading-relaxed" data-testid={`admin-buysong-${k}-conv`}>
                      Visit→Lyrics {s.view_to_lyric_pct ?? 0}% · Lyrics→Paid {s.lyric_to_paid_pct ?? 0}% · Visit→Paid {s.view_to_paid_pct ?? 0}%
                    </p>
                  </div>
                );
              })}
            </div>
          ) : buySongErr ? (
            <div className="mt-3 flex flex-col items-center gap-2 py-4" data-testid="admin-buysong-error">
              <p className="font-mono text-[10px] text-red-600 font-black uppercase tracking-wide">Couldn't load funnel stats</p>
              <button onClick={loadBuySong} className="font-mono text-[9px] font-black uppercase tracking-widest bg-charcoal text-gold border-2 border-charcoal rounded-full px-4 py-1.5 active:scale-95">Retry</button>
            </div>
          ) : (
            <div className="mt-3 flex items-center justify-center py-4"><Loader2 size={16} className="animate-spin text-charcoal" /></div>
          )}
        </div>

        {/* Master actions */}
        <div className="border-4 border-charcoal rounded-2xl bg-white p-4 shadow-[4px_4px_0px_#1A1A1A]" data-testid="admin-master-actions">
          <p className="font-mono text-[9px] font-black uppercase tracking-[0.2em] text-charcoal/50">Master Actions</p>
          <p className="font-sans font-bold text-charcoal mt-0.5 mb-3">Pipeline overrides & system-wide recovery</p>
          <button onClick={purge} disabled={purging} data-testid="admin-purge-btn" className="w-full flex items-center justify-center gap-2 bg-red-600 text-white font-mono text-[11px] font-black uppercase tracking-widest py-3 rounded-full border-2 border-charcoal shadow-[3px_3px_0px_#1A1A1A] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none disabled:opacity-60 mb-2">
            {purging ? <Loader2 size={15} className="animate-spin" /> : <Trash2 size={15} />} Purge Stuck Builds
          </button>
          {freed && (
            <div data-testid="admin-purge-result" className="flex items-center justify-center gap-2 rounded-xl border-2 border-charcoal/15 bg-cream py-2 mb-2">
              <HardDriveDownload size={13} className="text-charcoal/60" />
              <span className="font-mono text-[10px] font-black uppercase tracking-widest text-charcoal" data-testid="admin-purge-freed">{freed.scratchpads_removed} build(s) · {fmtBytes(freed.bytes_freed)} freed</span>
            </div>
          )}
        </div>

        {/* Alix Global Knowledge — founder system-prompt context + weight */}
        <div className="border-4 border-charcoal rounded-2xl bg-white p-4 shadow-[4px_4px_0px_#1A1A1A]" data-testid="admin-alix-knowledge-card">
          <p className="font-mono text-[9px] font-black uppercase tracking-[0.2em] text-charcoal/50 flex items-center gap-1 mb-2"><BrainCircuit size={12} /> Alix Global Knowledge</p>
          <textarea value={kbText} onChange={(e) => setKbText(e.target.value)} data-testid="admin-alix-knowledge-input" rows={4}
            placeholder="Global knowledge / system context Alix applies across all sessions…"
            className="w-full min-h-[96px] max-h-64 bg-cream border-2 border-charcoal rounded-xl px-3 py-2.5 font-mono text-xs text-charcoal outline-none focus:ring-2 focus:ring-gold resize-y" />
          <div className="mt-3 border-2 border-charcoal rounded-xl bg-cream p-3" data-testid="admin-kb-weight">
            <p className="font-mono text-[10px] font-black uppercase tracking-wide text-charcoal mb-2">Prompt Weight Multiplier</p>
            <div className="flex items-center gap-2">
              <button onClick={() => setKbWeight((w) => Math.max(0.1, Math.round((w - 0.1) * 10) / 10))} data-testid="admin-kb-weight-minus" className="w-9 h-9 grid place-items-center bg-white border-2 border-charcoal rounded-lg shadow-[2px_2px_0px_#1A1A1A] active:translate-x-[1px] active:translate-y-[1px] active:shadow-none"><Minus size={15} /></button>
              <input type="number" min="0.1" max="2.0" step="0.1" value={kbWeight}
                onChange={(e) => { const v = parseFloat(e.target.value); if (!Number.isNaN(v)) setKbWeight(Math.max(0.1, Math.min(2.0, v))); }}
                data-testid="admin-kb-weight-input" className="flex-1 min-w-0 text-center bg-white border-2 border-charcoal rounded-lg py-1.5 font-serif text-xl text-charcoal tabular-nums outline-none focus:ring-2 focus:ring-gold" />
              <button onClick={() => setKbWeight((w) => Math.min(2.0, Math.round((w + 0.1) * 10) / 10))} data-testid="admin-kb-weight-plus" className="w-9 h-9 grid place-items-center bg-white border-2 border-charcoal rounded-lg shadow-[2px_2px_0px_#1A1A1A] active:translate-x-[1px] active:translate-y-[1px] active:shadow-none"><Plus size={15} /></button>
            </div>
            <p className="mt-1 font-mono text-[8px] text-charcoal/45">Range 0.1 – 2.0 · default 1.0</p>
          </div>
          <button onClick={saveKnowledge} disabled={kbSaving} data-testid="admin-alix-knowledge-save" className="mt-3 w-full flex items-center justify-center gap-2 bg-gold text-black font-mono text-[11px] font-black uppercase tracking-widest py-3 rounded-full border-2 border-charcoal shadow-[3px_3px_0px_#1A1A1A] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none disabled:opacity-60">
            {kbSaving ? <Loader2 size={15} className="animate-spin" /> : <BrainCircuit size={15} />} Save Global Knowledge
          </button>
        </div>

        {/* OTP email test */}
        <div className="border-4 border-charcoal rounded-2xl bg-white p-4 shadow-[4px_4px_0px_#1A1A1A]" data-testid="admin-otp-card">
          <p className="font-mono text-[9px] font-black uppercase tracking-[0.2em] text-charcoal/50 flex items-center gap-1 mb-2"><Mail size={12} /> OTP Email Test</p>
          <input value={otp} onChange={(e) => setOtp(e.target.value)} data-testid="admin-otp-input" placeholder="recipient (blank = your admin email)" className="w-full bg-cream border-2 border-charcoal rounded-xl px-3 py-2.5 font-mono text-xs outline-none focus:ring-2 focus:ring-gold mb-2" />
          <button onClick={() => toast("Test email — coming soon")} data-testid="admin-send-email-btn" className="w-full flex items-center justify-center gap-2 bg-gold text-black font-mono text-[11px] font-black uppercase tracking-widest py-3 rounded-full border-2 border-charcoal shadow-[3px_3px_0px_#1A1A1A] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none"><Mail size={15} /> Send Test Email</button>
        </div>

        {/* System — live tabs bound to access-registry / payouts / system-config */}
        <div className="border-4 border-charcoal rounded-2xl bg-white p-4 shadow-[4px_4px_0px_#1A1A1A]" data-testid="admin-system-card">
          <div className="flex items-center justify-between mb-3">
            <p className="font-mono text-[9px] font-black uppercase tracking-[0.2em] text-charcoal/50 flex items-center gap-1"><Activity size={12} /> System</p>
            <button onClick={loadStreams} data-testid="admin-system-refresh" className="w-7 h-7 grid place-items-center rounded-lg bg-cream border-2 border-charcoal shadow-[1.5px_1.5px_0px_#1A1A1A] active:translate-x-[1px] active:translate-y-[1px] active:shadow-none"><RefreshCw size={12} /></button>
          </div>
          <div className="grid grid-cols-4 gap-2 mb-3">
            {TABS.map(({ k, label, Icon }) => (
              <button key={k} onClick={() => setTab(k)} data-testid={`admin-tab-${k}`}
                className={`flex flex-col items-center gap-1 rounded-xl border-2 border-charcoal py-2 font-mono text-[8px] font-black uppercase tracking-wider ${tab === k ? "bg-gold text-black shadow-[2px_2px_0px_#1A1A1A]" : "bg-white text-charcoal/50"}`}>
                <Icon size={14} /> {label}
              </button>
            ))}
          </div>

          <div className="min-h-[120px]" data-testid="admin-tab-content">
            {/* STATUS — maintenance toggle + rate-limit (system-config) + live counters */}
            {tab === "status" && (
              <div className="space-y-2.5" data-testid="admin-status-panel">
                <div className="grid grid-cols-2 gap-2">
                  <div className="border-2 border-charcoal rounded-xl bg-charcoal p-2.5" data-testid="status-subscribers">
                    <p className="font-mono text-[7.5px] uppercase tracking-[0.18em] text-gold/70">Subscribers</p>
                    <p className="font-serif text-2xl text-gold tabular-nums">{reg?.subscriber_count ?? "—"}</p>
                  </div>
                  <div className="border-2 border-charcoal rounded-xl bg-cream p-2.5" data-testid="status-pending-payouts">
                    <p className="font-mono text-[7.5px] uppercase tracking-[0.18em] text-charcoal/50">Pending Payouts</p>
                    <p className="font-serif text-2xl text-charcoal tabular-nums">${Number(pays?.pending_total ?? 0).toLocaleString()}</p>
                  </div>
                </div>
                {/* Maintenance toggle */}
                <div className="border-2 border-charcoal rounded-xl bg-cream p-3 flex items-center justify-between gap-2" data-testid="status-maintenance">
                  <div className="min-w-0">
                    <p className="font-mono text-[10px] font-black uppercase tracking-wide text-charcoal flex items-center gap-1"><Power size={12} /> Maintenance Mode</p>
                    <p className="font-mono text-[8px] text-charcoal/45 mt-0.5">Blocks standard client generation routes.</p>
                  </div>
                  <button onClick={() => saveCfg({ maintenance_mode: !cfg?.maintenance_mode })} data-testid="status-maintenance-toggle" disabled={cfg == null}
                    className={`shrink-0 w-14 h-8 rounded-full border-2 border-charcoal relative transition-colors ${cfg?.maintenance_mode ? "bg-gold" : "bg-white"} disabled:opacity-50`}>
                    <span className={`absolute top-0.5 w-6 h-6 rounded-full bg-charcoal transition-all ${cfg?.maintenance_mode ? "left-[26px]" : "left-0.5"}`} />
                  </button>
                </div>
                {/* Rate-limit */}
                <div className="border-2 border-charcoal rounded-xl bg-cream p-3" data-testid="status-ratelimit">
                  <p className="font-mono text-[10px] font-black uppercase tracking-wide text-charcoal mb-2">Rate Limit · reqs / account / min</p>
                  <div className="flex items-center gap-2">
                    <button onClick={() => saveCfg({ rate_limit_per_min: Math.max(1, rl - 1) })} data-testid="status-rl-minus" className="w-9 h-9 grid place-items-center bg-white border-2 border-charcoal rounded-lg shadow-[2px_2px_0px_#1A1A1A] active:translate-x-[1px] active:translate-y-[1px] active:shadow-none"><Minus size={15} /></button>
                    <span className="flex-1 text-center font-serif text-2xl text-charcoal tabular-nums" data-testid="status-rl-value">{rl}</span>
                    <button onClick={() => saveCfg({ rate_limit_per_min: Math.min(600, rl + 1) })} data-testid="status-rl-plus" className="w-9 h-9 grid place-items-center bg-white border-2 border-charcoal rounded-lg shadow-[2px_2px_0px_#1A1A1A] active:translate-x-[1px] active:translate-y-[1px] active:shadow-none"><Plus size={15} /></button>
                  </div>
                </div>
              </div>
            )}

            {/* USERS — subscribers + pending registrations (access-registry) */}
            {tab === "users" && (
              <div className="space-y-2" data-testid="admin-users-panel">
                <div className="flex items-center gap-2 border-2 border-charcoal rounded-xl bg-charcoal p-2.5">
                  <ShieldCheck size={22} className="text-gold" />
                  <div><p className="font-mono text-[7.5px] uppercase tracking-[0.18em] text-gold/70">Verified Subscribers</p>
                    <p className="font-serif text-2xl text-gold tabular-nums" data-testid="users-subscriber-count">{reg?.subscriber_count ?? "—"}</p></div>
                </div>
                <p className="font-mono text-[9px] font-black uppercase tracking-[0.16em] text-charcoal/60 flex items-center gap-1"><UserPlus size={12} /> Pending Registrations ({reg?.pending_count ?? 0})</p>
                <div className="space-y-1.5 max-h-40 overflow-y-auto no-scrollbar" data-testid="users-pending-list">
                  {reg == null ? <Loader2 size={14} className="animate-spin text-charcoal/40" />
                    : (reg.pending || []).length === 0 ? <p className="font-mono text-[10px] text-charcoal/40 py-2">No accounts awaiting sign-off.</p>
                    : reg.pending.map((u, i) => (
                      <div key={i} className="flex items-center justify-between border-2 border-charcoal/15 rounded-lg bg-cream px-2 py-1.5" data-testid={`users-pending-${i}`}>
                        <span className="font-mono text-[10px] text-charcoal truncate">{u.email || u.handle}</span>
                        <button onClick={() => adminApi.whitelist(u.email).then(() => { toast.success("Approved"); loadStreams(); }).catch(() => toast.error("Failed"))}
                          data-testid={`users-approve-${i}`} className="shrink-0 font-mono text-[8px] font-black uppercase tracking-wide bg-gold text-black border-2 border-charcoal rounded-full px-2 py-0.5">Approve</button>
                      </div>
                    ))}
                </div>

                {/* Ad Maker Tier Control */}
                <p className="font-mono text-[9px] font-black uppercase tracking-[0.16em] text-charcoal/60 flex items-center gap-1 pt-2"><ShieldCheck size={12} /> Ad Maker Tier Control ({(tiers || []).length})</p>
                <div className="space-y-1.5 max-h-64 overflow-y-auto no-scrollbar" data-testid="admin-tier-table">
                  {tiers == null ? <Loader2 size={14} className="animate-spin text-charcoal/40" />
                    : tiers.length === 0 ? <p className="font-mono text-[10px] text-charcoal/40 py-2">No accounts found.</p>
                    : tiers.map((u) => (
                      <div key={u.user_id} className="border-2 border-charcoal/15 rounded-lg bg-cream px-2 py-2 space-y-1.5" data-testid={`tier-row-${u.user_id}`}>
                        <p className="font-mono text-[10px] text-charcoal truncate">{u.handle || u.email || u.user_id}</p>
                        <div className="flex items-center gap-1.5">
                          <select value={u.category} onChange={(e) => setTierField(u.user_id, { category: e.target.value })}
                            data-testid={`tier-cat-${u.user_id}`}
                            className="flex-1 min-w-0 border-2 border-charcoal bg-white rounded-md px-1.5 py-1 font-mono text-[9px] uppercase font-black text-charcoal outline-none">
                            <option value="BUSINESS">Business</option>
                            <option value="PROFESSIONAL_CREATOR">Pro Creator</option>
                            <option value="INDIVIDUAL_RETAIL">Retail</option>
                          </select>
                          <button onClick={() => setTierField(u.user_id, { is_founder_exempt: !u.is_founder_exempt })}
                            data-testid={`tier-exempt-${u.user_id}`}
                            className={`shrink-0 font-mono text-[8px] font-black uppercase tracking-wide border-2 border-charcoal rounded-full px-2 py-1 ${u.is_founder_exempt ? "bg-gold text-black" : "bg-white text-charcoal/50"}`}>
                            {u.is_founder_exempt ? "Exempt ✓" : "Exempt"}
                          </button>
                          <button onClick={() => saveTier(u)} disabled={savingTier === u.user_id}
                            data-testid={`tier-save-${u.user_id}`}
                            className="shrink-0 font-mono text-[8px] font-black uppercase tracking-wide bg-charcoal text-white border-2 border-charcoal rounded-full px-2 py-1 disabled:opacity-60">
                            {savingTier === u.user_id ? "…" : "Save"}
                          </button>
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            )}
            {tab === "locks" && (
              <div className="space-y-2" data-testid="admin-locks-panel">
                <p className="font-mono text-[9px] font-black uppercase tracking-[0.16em] text-charcoal/60 flex items-center gap-1"><Lock size={12} /> Access Bypass Whitelist ({(reg?.whitelist || []).length})</p>
                {reg == null ? <Loader2 size={14} className="animate-spin text-charcoal/40" />
                  : (reg.whitelist || []).length === 0 ? <p className="font-mono text-[10px] text-charcoal/40 py-2">No active bypass locks.</p>
                  : (
                    <div className="flex flex-wrap gap-1.5" data-testid="locks-whitelist-chips">
                      {reg.whitelist.map((w) => (
                        <span key={w} className="font-mono text-[8px] bg-charcoal text-gold px-2 py-1 rounded-full border border-gold break-all" data-testid={`locks-chip-${w}`}>{w}</span>
                      ))}
                    </div>
                  )}
                <p className="font-mono text-[8px] text-charcoal/45 mt-1">Manage entries from Business → Config → Bypass Override.</p>
              </div>
            )}

            {/* BANS — creator billing / payout holds ledger (payouts) */}
            {tab === "bans" && (
              <div className="space-y-2" data-testid="admin-bans-panel">
                <p className="font-mono text-[9px] font-black uppercase tracking-[0.16em] text-charcoal/60 flex items-center gap-1"><DollarSign size={12} /> Billing & Payout Ledger ({pays?.count ?? 0})</p>
                {pays == null ? <Loader2 size={14} className="animate-spin text-charcoal/40" />
                  : (pays.payouts || []).length === 0 ? <p className="font-mono text-[10px] text-charcoal/40 py-2">No billing holds or payouts on record.</p>
                  : (
                    <div className="space-y-1.5 max-h-40 overflow-y-auto no-scrollbar" data-testid="bans-payout-list">
                      {pays.payouts.map((p, i) => (
                        <div key={p.id || i} className="flex items-center gap-2 border-2 border-charcoal/15 rounded-lg bg-cream px-2 py-1.5" data-testid={`bans-payout-${i}`}>
                          <div className="min-w-0 flex-1"><p className="font-mono text-[10px] font-black text-charcoal truncate">{p.creator || "—"}</p>
                            <p className="font-mono text-[8px] uppercase text-charcoal/40">{p.status}</p></div>
                          <span className="font-serif text-base text-charcoal tabular-nums">${Number(p.amount || 0).toLocaleString()}</span>
                        </div>
                      ))}
                    </div>
                  )}
              </div>
            )}

            {tab === "ledger" && (
              <div className="bg-[#0d0d0d] rounded-xl p-3 -mx-1" data-testid="admin-ledger-panel">
                <ApiLedgerPanel />
              </div>
            )}
          </div>
        </div>

        {/* Business — lower-panel workspace (Payouts · Engine · Config · Tools) */}
        <BusinessCommandCard />
      </div>
    </div>
  );
}
